import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { ProtectedRoute } from '@/components/auth/protected-route';

// Lazy-loaded pages for code splitting
const Landing = lazy(() => import('@/pages/landing'));
const Login = lazy(() => import('@/pages/login'));
const Signup = lazy(() => import('@/pages/signup'));
const EntraCallback = lazy(() => import('@/pages/entra-callback'));
const Dashboard = lazy(() => import('@/pages/dashboard'));
const Customers = lazy(() => import('@/pages/customers'));
const CustomerDetail = lazy(() => import('@/pages/customer-detail'));
const Subscriptions = lazy(() => import('@/pages/subscriptions'));
const Transactions = lazy(() => import('@/pages/transactions'));
const PaymentMethods = lazy(() => import('@/pages/payment-methods'));
const Invoices = lazy(() => import('@/pages/invoices'));
const Dunning = lazy(() => import('@/pages/dunning'));
const Collections = lazy(() => import('@/pages/collections'));
const CollectionRunDetail = lazy(() => import('@/pages/collection-run-detail'));
const Alerts = lazy(() => import('@/pages/alerts'));
const Users = lazy(() => import('@/pages/users'));
const Settings = lazy(() => import('@/pages/settings'));
const TenantList = lazy(() => import('@/pages/tenant-list'));
const TenantDetail = lazy(() => import('@/pages/tenant-detail'));

/** Shared suspense fallback shown while lazy chunks load. */
function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <span className="material-symbols-outlined animate-spin text-primary text-4xl">
        progress_activity
      </span>
    </div>
  );
}

/** Wraps a lazy page component in Suspense. */
function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Public route — landing page (unauthenticated)
  {
    path: '/landing',
    element: withSuspense(Landing),
  },

  // Public route — login (no auth guard)
  {
    path: '/login',
    element: withSuspense(Login),
  },

  // Public route — signup (no auth guard)
  {
    path: '/signup',
    element: withSuspense(Signup),
  },

  // Public route — OIDC callback (no auth guard)
  {
    path: '/auth/callback',
    element: withSuspense(EntraCallback),
  },

  // Protected routes — wrapped in auth guard + app layout shell
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: withSuspense(Dashboard) },
          { path: 'customers', element: withSuspense(Customers) },
          { path: 'customers/:id', element: withSuspense(CustomerDetail) },
          { path: 'subscriptions', element: withSuspense(Subscriptions) },
          { path: 'transactions', element: withSuspense(Transactions) },
          { path: 'payment-methods', element: withSuspense(PaymentMethods) },
          { path: 'invoices', element: withSuspense(Invoices) },
          { path: 'dunning', element: withSuspense(Dunning) },
          { path: 'collections', element: withSuspense(Collections) },
          { path: 'collections/:runId', element: withSuspense(CollectionRunDetail) },
          { path: 'alerts', element: withSuspense(Alerts) },
          { path: 'users', element: withSuspense(Users) },
          { path: 'settings', element: withSuspense(Settings) },
          { path: 'tenants', element: withSuspense(TenantList) },
          { path: 'tenants/:tenantId', element: withSuspense(TenantDetail) },
        ],
      },
    ],
  },

  // Catch-all — redirect unknown paths to dashboard
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
